import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CATEGORIES, usePerms } from "../lib/perms";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const LISTS = [
  ["no_student", "No student link"], ["unassigned", "Unassigned officer"], ["missing_category", "Missing program category"],
  ["missing_course", "Missing course name"], ["missing_intake", "Missing intake"], ["overdue_followups", "Overdue follow-ups"],
  ["duplicates", "Duplicate candidates"], ["student_review", "Student identity review"],
];

function CategoryPicker({ rows, picked, setPicked, onDone }) {
  const [cat, setCat] = useState("");
  const [saving, setSaving] = useState(false);
  const all = picked.length === rows.length;
  const apply = async () => {
    if (!cat || !picked.length) return toast.error("Tick applications and choose a category");
    if (!window.confirm(`Set ${picked.length} application(s) to ${cat}?`)) return;
    setSaving(true);
    try {
      const r = await api.post("/applications/bulk", { action: "set_category", value: cat, mode: "selected", ids: picked, confirm: true });
      toast.success(`${r.data.changed} application(s) set to ${cat}`); setCat(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set category"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="attn-category-picker" className="flex flex-wrap items-center gap-2 rounded-lg border border-orange-200 bg-orange-50/60 px-3 py-2 text-sm">
      <label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" data-testid="attn-pick-all" checked={all} onChange={() => setPicked(all ? [] : rows.map(r => r.id))} /> Select all ({rows.length})</label>
      <span data-testid="attn-picked-count" className="text-xs text-slate-600">{picked.length} selected</span>
      <select data-testid="attn-category-select" value={cat} onChange={e => setCat(e.target.value)} className="h-8 px-2 border rounded bg-white text-xs ml-auto">
        <option value="">Choose category…</option>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <button data-testid="attn-category-apply" disabled={saving} onClick={apply} className="h-8 px-3 rounded bg-orange-500 hover:bg-orange-600 text-white text-xs disabled:opacity-50">{saving ? "Saving…" : "Set category"}</button>
    </div>
  );
}

function ReviewActions({ r, onDone }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setOpts([]); return; }
    const t = setTimeout(() => { api.get("/b2b/students", { params: { q } }).then(x => setOpts(x.data.slice(0, 5))); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const resolve = async (student_id) => {
    try { await api.post(`/b2b/review-items/${r.review_id}/resolve`, student_id ? { student_id } : {}); toast.success(student_id ? "Linked to profile" : "Kept as is"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not resolve"); }
  };
  return (
    <div className="relative flex items-center gap-1 shrink-0">
      <input data-testid={`review-search-${r.id}`} value={q} onChange={e => setQ(e.target.value)} placeholder="Link to student…" className="h-7 w-36 px-2 text-xs border rounded" />
      <button data-testid={`review-keep-${r.id}`} onClick={() => resolve(null)} className="text-xs text-slate-600 underline">Keep as is</button>
      {opts.length > 0 && <div className="absolute right-0 top-8 z-10 w-64 bg-white border rounded shadow">
        {opts.map(s => <button key={s.id} data-testid={`review-link-${s.id}`} onClick={() => resolve(s.id)} className="block w-full text-left px-2 py-1 text-xs hover:bg-orange-50">{s.student_name} · {s.student_code} · {s.email || "no email"}</button>)}
      </div>}
    </div>
  );
}

export default function NeedsAttention({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("missing_category");
  const nav = useNavigate();
  const reload = () => api.get("/b2b/needs-attention").then(r => setData(r.data));
  useEffect(() => { if (open) { setData(null); reload(); } }, [open]); // eslint-disable-line
  const rows = data?.lists?.[tab] || [];
  const perms = usePerms();
  const [picked, setPicked] = useState([]);
  useEffect(() => { setPicked([]); }, [tab, data]);
  const pickable = tab === "missing_category" && perms.edit;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="needs-attention-dialog" className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-font">Needs Attention</DialogTitle>
          <DialogDescription>Open applications that are missing something. They stay fully usable while on this list.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {LISTS.map(([k, l]) => (
            <button key={k} data-testid={`attn-tab-${k}`} onClick={() => setTab(k)}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${tab === k ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-orange-300"}`}>
              {l} <b>{data ? data.counts[k] : "…"}</b>
            </button>
          ))}
        </div>
        {pickable && rows.length > 0 && <CategoryPicker rows={rows} picked={picked} setPicked={setPicked} onDone={reload} />}
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          {!data && <div className="p-4 text-sm text-slate-500">Loading…</div>}
          {data && rows.length === 0 && <div className="p-4 text-sm text-slate-500">Nothing here.</div>}
          {rows.map(r => (
            <div key={r.id + (r.review_id || "")} className="flex items-center gap-2 px-4 py-2 hover:bg-orange-50 text-sm">
              {pickable && <input type="checkbox" data-testid={`attn-pick-${r.id}`} checked={picked.includes(r.id)}
                onChange={() => setPicked(p => p.includes(r.id) ? p.filter(x => x !== r.id) : [...p, r.id])} />}
              <button data-testid={`attn-row-${r.id}`} onClick={() => { onOpenChange(false); nav(`/applications/${r.id}`); }} className="flex-1 min-w-0 text-left flex items-center gap-3">
                <span className="mono text-xs text-slate-500 shrink-0">{r.application_id}</span>
                <span className="font-medium text-slate-900 truncate">{r.student_name}</span>
                <span className="text-xs text-slate-500 truncate ml-auto">{tab === "missing_category" && r.course ? `${r.course} · ` : ""}{r.college}</span>
              </button>
              {r.review_id && <ReviewActions r={r} onDone={reload} />}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
